import React, { Component, createRef } from 'react';
import { inject, observer } from 'mobx-react';
import { datadogLogs } from '@datadog/browser-logs';
import PropTypes from 'prop-types';
import loadjs from 'loadjs';
import MPGSClient from '../../adapters/MPGSClient';
import './RobinsonsBankPaymentPageComponent.scss';

const AUTH_PAYER_MAX_RETRY = process.env.REACT_APP_MPGS_AUTH_PAYER_MAX_RETRIES;
const CONTAINER_ID = 'checkout-3ds';
class RobinsonsBankPaymentComponent extends Component {
  constructor(props) {
    super(props);

    this.contentRef = createRef();
    this.isWhitelisted = this.isWhitelisted();
    this.authPayerTimeout = this.getAuthPayerTimeout();
  }

  componentDidMount() {
    const { sourceStore } = this.props;

    if (sourceStore.source.type === 'three_d_secure') {
      sourceStore.setPageLoading(true);
      sourceStore.setPageLoadingText(
        'Kindly wait while we set up the authentication process for you.'
      );

      loadjs(process.env.REACT_APP_MPGS_JS_URL, () => {
        this.client = new MPGSClient();

        this.configureApi();
      });
    }
  }

  getAuthPayerTimeout() {
    return this.isWhitelisted
      ? parseInt(process.env.REACT_APP_MPGS_AUTH_PAYER_TIMEOUT, 10) || 2000
      : 10000;
  }

  canRetryRequest(error, attempt) {
    return (
      error.cause === 'SERVER_BUSY' && this.isWhitelisted && attempt + 1 <= AUTH_PAYER_MAX_RETRY
    );
  }

  configureApi() {
    const { sourceStore } = this.props;

    datadogLogs.logger.info('MPGS config initialize', {
      data: { source_id: sourceStore.source.id }
    });

    this.client.configure(
      sourceStore.source.redirect.card_mid,
      sourceStore.source.redirect.provider_auth_id,
      CONTAINER_ID,
      () => {
        if (this.client.isConfigured()) {
          this.initiateAuthentication();

          // Event handler on close
          this.client.onEvent('AFTER_AUTHENTICATE_PAYER', d => {
            let data = d;

            if (typeof data === 'undefined') {
              data = { source_id: sourceStore.source.id };
            } else {
              data.source_id = sourceStore.source.id;
            }

            datadogLogs.logger.info('MPGS 3DS page close', { data });
          });
        } else {
          datadogLogs.logger.info('MPGS config setup failure', {
            data: { source_id: sourceStore.source.id }
          });

          sourceStore.fail({
            error_code: 'FAILED_TO_CONFIGURE_API',
            error_description: 'Failed to configure ThreeDsClient.'
          });
          sourceStore.setPageLoadingText(null);
        }
      }
    );
  }

  authenticatePayer(attempt) {
    const { sourceStore } = this.props;

    datadogLogs.logger.info(`MPGS 3DS page setup - authenticate payer attempt : ${attempt}`, {
      data: { source_id: sourceStore.source.id }
    });

    sourceStore.setPageLoadingText(
      'We are almost done setting up. You may be redirected to your bank’s authentication page.'
    );

    this.client.authenticatePayer(
      sourceStore.source.redirect.reference_id,
      sourceStore.source.redirect.provider_transaction_id,
      d => {
        const data = d;
        data.source_id = sourceStore.source.id;

        if (data.error) {
          const { error } = data;

          if (this.canRetryRequest(error, attempt)) {
            setTimeout(() => {
              this.authenticatePayer(attempt + 1);
            }, this.authPayerTimeout);
          } else {
            datadogLogs.logger.info('MPGS 3DS page render error', { data });

            this.sourceFail(error, 'authenticatePayer');
          }
        } else {
          const html = document.createRange().createContextualFragment(data.htmlRedirectCode);

          datadogLogs.logger.info('MPGS 3DS page open', { data });

          this.contentRef.current.appendChild(html);
          if (this.contentRef.current.innerHTML) {
            sourceStore.setPageLoading(false);
            sourceStore.setPageLoadingText(null);
          }
        }
      }
    );
  }

  initiateAuthentication() {
    const { sourceStore } = this.props;

    datadogLogs.logger.info('MPGS config setup complete', {
      data: { source_id: sourceStore.source.id }
    });

    sourceStore.setPageLoadingText(
      'Securely passing your device information to your bank for checking.'
    );

    this.client.initiateAuthentication(
      sourceStore.source.redirect.reference_id,
      sourceStore.source.redirect.provider_transaction_id,
      d => {
        const data = d;
        data.source_id = sourceStore.source.id;

        if (data && data.error) {
          const { error } = data;

          datadogLogs.logger.info('MPGS 3DS initiateAuthentication failed', { data });

          this.sourceFail(error, 'initiateAuthentication');
          sourceStore.setPageLoadingText(null);
        } else {
          datadogLogs.logger.info('MPGS 3DS initiateAuthentication success', { data });

          switch (data.gatewayRecommendation) {
            case 'PROCEED':
              setTimeout(() => {
                this.authenticatePayer(1);
              }, this.authPayerTimeout);

              break;
            case 'DO_NOT_PROCEED':
              datadogLogs.logger.info('MPGS 3DS DO_NOT_PROCEED recommendation', { data });

              sourceStore.fail({
                error_code: 'DO_NOT_PROCEED',
                error_description:
                  'Gateway recommendation is DO_NOT_PROCEED after initiating authentication.'
              });

              sourceStore.setPageLoadingText(null);

              break;
            default:
          }
        }
      }
    );
  }

  isWhitelisted() {
    const { sourceStore } = this.props;

    return (process.env.REACT_APP_MPGS_WHITELISTED_ORG ?? '')
      .split(',')
      .map(s => s.trim())
      .filter(org => !!org)
      .includes(sourceStore.source.organizationId);
  }

  // eslint-disable-next-line class-methods-use-this
  createErrorDescription(error) {
    let errorDesc = error.field ? `${error.field} - ` : '';

    errorDesc += error.explanation ? error.explanation : error.validationType;

    return errorDesc;
  }

  sourceFail(error, action) {
    const { sourceStore } = this.props;

    let errorCode = error.cause;
    let errorDesc = this.createErrorDescription(error);

    if (!error.cause) {
      errorCode = 'MPGS_CLIENTJS_ERROR';
      errorDesc = `MPGS 3DS ${action} failed`;
    }

    sourceStore.fail({
      error_code: errorCode,
      error_description: errorDesc
    });
  }

  render() {
    const { sourceStore } = this.props;

    return (
      <div className="live-payment-page">
        {sourceStore.source.type !== 'three_d_secure' && (
          <p className="SubHead">
            Source&nbsp;
            <span>{sourceStore.source.id}</span>
            &nbsp;is not applicable for processing.
          </p>
        )}
        <div id={CONTAINER_ID}> </div>
        <div ref={this.contentRef} />
      </div>
    );
  }
}

RobinsonsBankPaymentComponent.propTypes = {
  sourceStore: PropTypes.shape({
    source: PropTypes.shape({
      id: PropTypes.string,
      organizationId: PropTypes.string,
      type: PropTypes.string,
      redirect: PropTypes.shape({
        card_mid: PropTypes.string,
        provider_auth_id: PropTypes.string,
        provider_auth_url: PropTypes.string,
        provider_transaction_id: PropTypes.string,
        reference_id: PropTypes.string
      })
    }),
    charge: PropTypes.func.isRequired,
    fail: PropTypes.func.isRequired,
    setPageLoading: PropTypes.func.isRequired,
    setPageLoadingText: PropTypes.func.isRequired
  }).isRequired
};

export default inject(stores => {
  return {
    sourceStore: stores.store.source
  };
})(observer(RobinsonsBankPaymentComponent));
